/**
 * Morning Harvest Briefing API Service
 * TanStack Query hooks for audio briefings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import { api } from './api'; // Uncomment when using real API
import type { DailyBriefing, BriefingSegment, VoiceCommandType } from '../types/audio';

// Query Keys
export const briefingKeys = {
  all: ['briefings'] as const,
  daily: (date?: string) => [...briefingKeys.all, 'daily', date ?? 'today'] as const,
  segment: (id: string) => [...briefingKeys.all, 'segment', id] as const,
  history: () => [...briefingKeys.all, 'history'] as const,
};

// Types
export interface BriefingPreferences {
  voiceId: string;
  includeTypes: string[];
  maxDuration: number; // minutes
  summarizationLevel: 'brief' | 'normal' | 'detailed';
}

export interface SegmentAction {
  segmentId: string;
  action: VoiceCommandType;
  completedAt?: Date;
  notes?: string;
}

export interface BriefingProgress {
  briefingId: string;
  currentSegmentIndex: number;
  completedSegments: string[];
  lastPlayedAt: Date;
}

// API Functions
async function fetchDailyBriefing(date?: string): Promise<DailyBriefing> {
  // In production:
  // return api.get<DailyBriefing>('/briefings/daily', { params: { date } });

  await new Promise((resolve) => setTimeout(resolve, 400));

  const today = date ? new Date(date) : new Date();

  const segments: BriefingSegment[] = [
    {
      id: 'seg-1',
      type: 'summary',
      title: 'Daily Overview',
      content: 'Good morning. Today you have 4 pending decisions, 2 client meetings, and 1 deadline approaching. Your stress level is moderate based on yesterday\'s readings. Let\'s start with the most urgent items.',
      priority: 'normal',
      duration: 25,
      audioUrl: '/audio/briefing-summary.mp3',
    },
    {
      id: 'seg-2',
      type: 'deadline',
      title: 'Discovery Deadline - Martinez v. Acme',
      content: 'The discovery deadline for Martinez v. Acme Corp is in 5 days. Interrogatories have been drafted but require your final review. The opposing counsel has requested an extension which may impact trial scheduling.',
      priority: 'high',
      duration: 30,
      relatedMatterId: 'matter-123',
      audioUrl: '/audio/briefing-deadline.mp3',
    },
    {
      id: 'seg-3',
      type: 'billing',
      title: 'Invoice Approval Required',
      content: 'Three invoices totaling $28,450 are awaiting your approval. The Thompson Estate final billing of $12,450 has been outstanding for 2 days. Client has pre-approved via email.',
      priority: 'high',
      duration: 20,
      relatedDecisionId: 'decision-1',
      audioUrl: '/audio/briefing-billing.mp3',
    },
    {
      id: 'seg-4',
      type: 'task',
      title: 'Deposition Summary Ready',
      content: 'The AI has completed summarizing the 6-hour deposition from the Martinez case. Key admissions have been flagged regarding product defect timeline. Review recommended before today\'s 2pm strategy meeting.',
      priority: 'normal',
      duration: 25,
      relatedDecisionId: 'decision-3',
      audioUrl: '/audio/briefing-task.mp3',
    },
    {
      id: 'seg-5',
      type: 'alert',
      title: 'Settlement Offer Received',
      content: 'Opposing counsel in Martinez v. Acme has submitted a settlement offer of $125,000. Based on previous discussions, client\'s minimum acceptable was $150,000. Response deadline is Thursday.',
      priority: 'high',
      duration: 22,
      relatedDecisionId: 'decision-4',
      audioUrl: '/audio/briefing-alert.mp3',
    },
    {
      id: 'seg-6',
      type: 'insight',
      title: 'Weekly Productivity Insight',
      content: 'Your average decision processing time has improved by 15% this week. You\'ve handled 34 decisions with a 97% accuracy rate based on post-decision outcomes. Consider delegating low-priority document reviews to increase throughput.',
      priority: 'low',
      duration: 28,
      audioUrl: '/audio/briefing-insight.mp3',
    },
  ];

  return {
    id: `briefing-${today.toISOString().split('T')[0]}`,
    date: today,
    segments,
    totalDuration: segments.reduce((sum, s) => sum + s.duration, 0),
    generatedAt: new Date(Date.now() - 3600000), // 1 hour ago
    voiceId: 'alloy',
  };
}

async function generateBriefing(_preferences: BriefingPreferences): Promise<DailyBriefing> {
  // return api.post<DailyBriefing>('/briefings/generate', _preferences);

  await new Promise((resolve) => setTimeout(resolve, 2000));

  // Return newly generated briefing (mock ignores preferences for now)
  return fetchDailyBriefing();
}

async function processSegmentAction(action: SegmentAction): Promise<void> {
  // return api.post('/briefings/segments/action', action);

  await new Promise((resolve) => setTimeout(resolve, 200));

  console.log('Processing segment action:', action);
}

async function saveBriefingProgress(progress: BriefingProgress): Promise<void> {
  // return api.post('/briefings/progress', progress);

  await new Promise((resolve) => setTimeout(resolve, 100));

  // Also save to localStorage for offline access
  localStorage.setItem(`briefing-progress-${progress.briefingId}`, JSON.stringify(progress));
}

async function fetchBriefingHistory(): Promise<DailyBriefing[]> {
  // return api.get<DailyBriefing[]>('/briefings/history', { params: { limit: 7 } });

  await new Promise((resolve) => setTimeout(resolve, 300));

  // Return last 7 days of briefings
  const history: DailyBriefing[] = [];
  for (let i = 1; i <= 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    history.push({
      id: `briefing-${date.toISOString().split('T')[0]}`,
      date,
      segments: [],
      totalDuration: Math.floor(Math.random() * 120) + 60,
      generatedAt: date,
      voiceId: 'alloy',
    });
  }

  return history;
}

// Hooks

/**
 * Fetch today's briefing
 */
export function useDailyBriefing(date?: string) {
  return useQuery({
    queryKey: briefingKeys.daily(date),
    queryFn: () => fetchDailyBriefing(date),
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Generate a new briefing
 */
export function useGenerateBriefing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateBriefing,

    onSuccess: (newBriefing) => {
      queryClient.setQueryData(briefingKeys.daily(), newBriefing);
    },
  });
}

/**
 * Process a segment action (handle, skip, snooze)
 */
export function useSegmentAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processSegmentAction,

    // Optimistic update
    onMutate: async (action) => {
      const briefing = queryClient.getQueryData<DailyBriefing>(briefingKeys.daily());

      if (briefing) {
        // Update segment state optimistically
        const updatedSegments = briefing.segments.map((seg) =>
          seg.id === action.segmentId
            ? { ...seg, processed: true, processedAction: action.action }
            : seg
        );

        queryClient.setQueryData<DailyBriefing>(briefingKeys.daily(), {
          ...briefing,
          segments: updatedSegments,
        });
      }

      return { previousBriefing: briefing };
    },

    onError: (_err, _action, context) => {
      if (context?.previousBriefing) {
        queryClient.setQueryData(briefingKeys.daily(), context.previousBriefing);
      }
    },
  });
}

/**
 * Save playback progress
 */
export function useSaveBriefingProgress() {
  return useMutation({
    mutationFn: saveBriefingProgress,
  });
}

/**
 * Fetch briefing history
 */
export function useBriefingHistory() {
  return useQuery({
    queryKey: briefingKeys.history(),
    queryFn: fetchBriefingHistory,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Get saved progress from localStorage
 */
export function getSavedProgress(briefingId: string): BriefingProgress | null {
  const saved = localStorage.getItem(`briefing-progress-${briefingId}`);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  }
  return null;
}

export default {
  useDailyBriefing,
  useGenerateBriefing,
  useSegmentAction,
  useSaveBriefingProgress,
  useBriefingHistory,
  getSavedProgress,
};
